import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Target, Clock, Play } from 'lucide-react';

// Default track info for known certification tracks
const DEFAULT_TRACK_INFO = {
  PMP: { name: 'PMP', fullName: 'Project Management Professional', questions: 180, time: 230, color: 'bg-[#0047AB]' },
  CAPM: { name: 'CAPM', fullName: 'Certified Associate in Project Management', questions: 150, time: 180, color: 'bg-purple-600' },
  PSM: { name: 'Scrum Master', fullName: 'Professional Scrum Master', questions: 80, time: 60, color: 'bg-green-600' },
  PSPO: { name: 'Product Owner', fullName: 'Professional Scrum Product Owner', questions: 80, time: 60, color: 'bg-purple-700' }
};

// Get track info with fallback for custom tracks
const getTrackInfo = (trackCode) => {
  if (DEFAULT_TRACK_INFO[trackCode]) {
    return DEFAULT_TRACK_INFO[trackCode];
  }
  // Fallback for custom tracks
  return {
    name: trackCode,
    fullName: trackCode,
    questions: 50,
    time: 60,
    color: 'bg-indigo-600'
  };
};

const TrackSelector = ({ user, hasPremiumAccess, onTrackChange }) => {
  const navigate = useNavigate();
  
  const currentTrack = user?.selected_track ? getTrackInfo(user.selected_track) : null;
  const userTrackAccess = user?.track_access || [];
  const userTracks = userTrackAccess.filter(t => t.is_active).map(t => t.track);
  const hasMultipleTracks = userTracks.length > 1;

  if (!currentTrack) return null;

  return (
    <Card className="mb-6 bg-gradient-to-br from-slate-900 to-slate-800 text-white overflow-hidden">
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex-1">
            {hasMultipleTracks ? (
              <div className="mb-2">
                <div className="flex flex-wrap gap-2">
                  {userTracks.map(track => {
                    const trackInfo = getTrackInfo(track);
                    return (
                      <Button
                        key={track}
                        variant={user?.selected_track === track ? "default" : "outline"}
                        size="sm"
                        onClick={() => onTrackChange(track)}
                        className={user?.selected_track === track 
                          ? `${trackInfo.color} text-white border-0` 
                          : 'border-white/30 text-white hover:bg-white/10'}
                        data-testid={`track-${track}-btn`}
                      >
                        {trackInfo.name}
                      </Button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className={`inline-flex px-3 py-1 rounded-full text-sm font-medium text-white ${currentTrack.color} mb-2`}>
                {currentTrack.name}
              </div>
            )}
            <h2 className="text-xl font-bold">{currentTrack.fullName}</h2>
            <div className="flex items-center gap-4 text-slate-300 text-sm mt-1">
              <span className="flex items-center gap-1">
                <Target className="w-4 h-4" />
                {currentTrack.questions} questions
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {currentTrack.time} minutes
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => navigate('/training')}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="start-training-btn"
            >
              <Play className="w-4 h-4 mr-2" />
              Start Training
            </Button>
            {hasPremiumAccess && (
              <Button 
                variant="outline"
                onClick={() => navigate('/exam')}
                className="border-white/30 text-white hover:bg-white/10"
                data-testid="start-exam-btn"
              >
                <Clock className="w-4 h-4 mr-2" />
                Exam Simulation
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TrackSelector;
